/**
 * ErrorBoundary - React error boundary component
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs the errors, and displays a fallback UI instead of crashing
 * the entire application.
 *
 * @see https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportClientError, makeErrorCode, type ClientErrorVariant } from '@smallbiz/ui-primitives';
import { trackError } from '../../lib/analytics';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Custom fallback UI to show when an error occurs */
  fallback?: ReactNode;
  /** Called when an error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Reset key - changing this will reset the error state */
  resetKey?: string | number;
  /** Telemetry label for the funnel (#5310). Defaults to 'ErrorBoundary'. */
  component?: string;
  /** Funnel severity/scope (#5310). Defaults to 'app' (root usage). */
  variant?: Extract<ClientErrorVariant, 'app' | 'page' | 'inline'>;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorRef: string | null;
}

// #2986 I — the message browsers throw for a stale lazy-loaded chunk: the
// deployed index.html still references a hashed chunk file a newer deploy
// removed. Covers the Vite/Chromium and Firefox/Safari wordings.
const STALE_CHUNK_ERROR = /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i;

// One reload per session, so a chunk failure that a reload does NOT fix
// (a real outage) falls through to the fallback UI instead of loop-reloading.
const STALE_CHUNK_RELOAD_KEY = 'sbi-stale-chunk-reload';

function hasReloadedForStaleChunk(): boolean {
  try {
    return sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY) === '1';
  } catch {
    return false;
  }
}

function markReloadedForStaleChunk(): void {
  try {
    sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, '1');
  } catch {
    // Private mode / quota — the reload still runs once, just unguarded.
  }
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorRef: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // #2986 I — a stale chunk after a deploy self-heals with one reload,
    // which fetches the current index.html and its current chunk hashes.
    if (
      typeof window !== 'undefined' &&
      STALE_CHUNK_ERROR.test(error.message) &&
      !hasReloadedForStaleChunk()
    ) {
      markReloadedForStaleChunk();
      window.location.reload();
      return;
    }

    // Always clear any scroll lock that may have been left by the mobile menu
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }

    const errorCode = makeErrorCode();
    this.setState({ errorRef: errorCode.errorRef });

    // Log the error to legacy analytics with the same lookup code.
    trackError(error, {
      componentStack: errorInfo.componentStack || undefined,
      source: 'ErrorBoundary',
      error_code: errorCode.errorCode,
      error_ref: errorCode.errorRef,
      error_timestamp: errorCode.errorTimestamp,
    });

    // Unified client funnel (#5310 / #5309): render-time crash → CF Observability
    // [CLIENT-ERROR] + durable error_logs(source='client') + PostHog 102930, all
    // carrying the same EB- code. Shared with admin-sbi (this component is aliased there).
    reportClientError({
      component: this.props.component ?? 'ErrorBoundary',
      message: error.message,
      stack: error.stack ?? null,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      ...errorCode,
      variant: this.props.variant ?? 'app',
      errorType: error.name ?? null,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log to console in development
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // Reset error state when resetKey changes
    if (this.props.resetKey !== prevProps.resetKey && this.state.hasError) {
      this.setState({ hasError: false, error: null, errorRef: null });
    }
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null, errorRef: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Respect explicit fallback values (including null)
      if (this.props.fallback !== undefined) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <DefaultErrorFallback
          error={this.state.error}
          errorRef={this.state.errorRef}
          onRetry={this.handleRetry}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Default error fallback UI
 * Branded, user-friendly error message with retry option
 */
interface DefaultErrorFallbackProps {
  error: Error | null;
  errorRef: string | null;
  onRetry: () => void;
}

function DefaultErrorFallback({ error, errorRef, onRetry }: DefaultErrorFallbackProps) {
  const handleGoHome = () => {
    window.location.href = '/';
  };

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-linear-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white/10 backdrop-blur-lg rounded-2xl p-8 text-center border border-white/20 shadow-2xl">
        {/* Error icon */}
        <div className="mb-6">
          <div className="w-20 h-20 mx-auto bg-red-500/20 rounded-full flex items-center justify-center">
            <svg
              className="w-10 h-10 text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-white mb-2">Something went wrong</h1>

        {/* Description */}
        <p className="text-slate-300 mb-6">
          We encountered an unexpected error. Our team has been notified and is working on a fix.
        </p>

        {/* Error details (in development) */}
        {process.env.NODE_ENV === 'development' && error && (
          <div className="mb-6 p-3 bg-black/30 rounded-lg text-left">
            <p className="text-xs text-red-400 font-mono break-all">{error.message}</p>
          </div>
        )}

        {errorRef && (
          <p className="mb-6 text-xs text-slate-400">
            Error code: <code className="font-mono text-slate-200">{errorRef}</code>
          </p>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={onRetry}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
          >
            Try Again
          </button>
          <button
            onClick={handleReload}
            className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition-colors border border-white/20"
          >
            Reload Page
          </button>
          <button
            onClick={handleGoHome}
            className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition-colors border border-white/20"
          >
            Go Home
          </button>
        </div>

        {/* Contact info */}
        <p className="mt-6 text-sm text-slate-400">
          Need help?{' '}
          <a href="/contact" className="text-blue-400 hover:text-blue-300 underline">
            Contact support
          </a>
        </p>
      </div>
    </div>
  );
}

/**
 * Hook-based error boundary wrapper for functional components
 * Usage: wrap a component that might throw errors
 */
export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  fallback?: ReactNode
): React.FC<P> {
  const ComponentWithBoundary: React.FC<P> = (props) => (
    <ErrorBoundary fallback={fallback}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );

  ComponentWithBoundary.displayName = `withErrorBoundary(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`;

  return ComponentWithBoundary;
}

export default ErrorBoundary;
